/*
 * AdminMasterPolicy — Stage-1 Allianz Trade master-policy application queue.
 *
 * Three-tab view (Pending / Approved / Declined) over the
 * /api/admin/master-policy-applications endpoints.
 *
 * Approve drawer captures the broker-issued policy_id + BU + dates + premium
 * rate (manually typed in by ops after the Allianz underwriter responds).
 * On approve, the backend writes to `tenants.allianz_settings.policy_id`
 * which auto-unlocks per-buyer cover requests (Stage 2 — already API-driven).
 *
 * Decline drawer captures the reason that gets surfaced on the seller's
 * /seller/insurance page along with a "Reapply" CTA.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  ShieldCheck,
  DollarSign,
  ShieldOff,
  Clock,
  RefreshCw,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Loader2,
  Building2,
} from "lucide-react";

const TABS = [
  { id: "pending_review",   label: "Pending",          icon: Clock,        tone: "amber" },
  { id: "reapply_pending",  label: "Reapply pending",  icon: RefreshCw,    tone: "amber" },
  { id: "approved",         label: "Approved",         icon: CheckCircle2, tone: "emerald" },
  { id: "declined",         label: "Declined",         icon: XCircle,      tone: "rose" },
];

const TONE_CLASSES = {
  amber:   { dot: "bg-amber-500",   text: "text-amber-700",   bg: "bg-amber-50/60",   border: "border-amber-300" },
  emerald: { dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50/60", border: "border-emerald-300" },
  rose:    { dot: "bg-rose-500",    text: "text-rose-700",    bg: "bg-rose-50/60",    border: "border-rose-300" },
};

export default function AdminMasterPolicy() {
  const [tab, setTab] = useState("pending_review");
  const [data, setData] = useState({ items: [], by_status: {} });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/master-policy-applications");
      setData(d || { items: [], by_status: {} });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get("/admin/master-policy-applications");
        if (!cancelled) setData(d || { items: [], by_status: {} });
      } catch (e) {
        if (!cancelled) toast.error(formatErr(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const items = useMemo(
    () => (data.items || []).filter((a) => a.status === tab),
    [data.items, tab],
  );

  return (
    <div className="space-y-5 max-w-7xl" data-testid="admin-master-policy">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Allianz Master-Policy Applications
        </h1>
        <p className="text-[12.5px] text-foreground/55 max-w-2xl">
          Stage 1 of the Allianz Trade onboarding flow. Sellers submit a buyer
          roster + EIN / legal address / annual sales volume. The verqo ops
          team forwards to the Allianz broker, then enters the issued policy
          ID here to unlock per-buyer cover provisioning.
        </p>
      </header>

      {/* Tabs */}
      <div
        className="flex gap-1 border-b overflow-x-auto"
        data-testid="mp-admin-tabs"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const count = data.by_status?.[t.id] || 0;
          const active = tab === t.id;
          const tone = TONE_CLASSES[t.tone];
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`mp-admin-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm border-b-2 whitespace-nowrap transition ${
                active
                  ? "border-foreground text-foreground"
                  : "border-transparent text-foreground/55 hover:text-foreground/80"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
              {count > 0 && (
                <span
                  className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-medium ${tone.bg} ${tone.text}`}
                  data-testid={`mp-admin-count-${t.id}`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2 py-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={reload}
            data-testid="mp-admin-refresh"
            className="text-[12px] gap-1"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-3" data-testid="mp-admin-list">
        {loading ? (
          <div className="flex items-center gap-2 text-[13px] text-foreground/55 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading applications…
          </div>
        ) : items.length === 0 ? (
          <div
            className="rounded-2xl border border-dashed border-foreground/15 bg-foreground/[0.02] px-6 py-12 text-center"
            data-testid="mp-admin-empty"
          >
            <ShieldOff className="w-6 h-6 text-foreground/30 mx-auto mb-2" />
            <div className="text-[13px] font-medium text-foreground/70">
              No applications in this state
            </div>
            <div className="text-[11.5px] text-foreground/45 mt-1">
              Sellers that opt into Allianz coverage will show up here.
            </div>
          </div>
        ) : (
          items.map((a) => (
            <ApplicationRow
              key={a.id}
              app={a}
              onOpen={() => setSelected(a)}
            />
          ))
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <DetailDialog
          app={selected}
          open={!!selected}
          onClose={() => setSelected(null)}
          onApprove={() => {
            setApproveOpen(true);
          }}
          onDecline={() => {
            setDeclineOpen(true);
          }}
          onQuote={() => {
            setQuoteOpen(true);
          }}
        />
      )}

      {/* Quote drawer — admin enters Allianz premium + coverage limit */}
      {selected && quoteOpen && (
        <QuoteDialog
          app={selected}
          onClose={() => setQuoteOpen(false)}
          onQuoted={async () => {
            setQuoteOpen(false);
            setSelected(null);
            await reload();
          }}
        />
      )}

      {/* Approve drawer — only mount when open so state always resets */}
      {selected && approveOpen && (
        <ApproveDialog
          app={selected}
          onClose={() => setApproveOpen(false)}
          onApproved={async () => {
            setApproveOpen(false);
            setSelected(null);
            await reload();
          }}
        />
      )}

      {/* Decline drawer — only mount when open so state always resets */}
      {selected && declineOpen && (
        <DeclineDialog
          app={selected}
          onClose={() => setDeclineOpen(false)}
          onDeclined={async () => {
            setDeclineOpen(false);
            setSelected(null);
            await reload();
          }}
        />
      )}
    </div>
  );
}

// ─── Row ────────────────────────────────────────────────────────────────
function ApplicationRow({ app, onOpen }) {
  const totalNextVol = (app.buyers || []).reduce(
    (s, b) => s + (Number(b.annual_sales_volume_next_12mo) || 0),
    0,
  );
  return (
    <button
      onClick={onOpen}
      data-testid={`mp-admin-row-${app.id}`}
      className="w-full text-left rounded-2xl border border-foreground/10 bg-white/60 hover:bg-foreground/[0.025] hover:border-foreground/20 px-5 py-4 transition"
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-foreground/[0.05]">
          <Building2 className="w-5 h-5 text-foreground/60" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium text-[14px] truncate">
              {app.seller_company || app.seller_name || "Unknown seller"}
            </div>
            <span className="text-[10px] mono uppercase tracking-wider text-foreground/45">
              {app.id}
            </span>
            {app.reapply_count > 0 && (
              <span className="text-[10px] mono uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                reapply #{app.reapply_count}
              </span>
            )}
          </div>
          <div className="text-[12px] text-foreground/60 mt-0.5">
            {app.seller_email} · EIN {app.seller_ein || "—"}
          </div>
          <div className="mt-2 flex items-center gap-4 flex-wrap text-[11.5px]">
            <span className="text-foreground/65">
              Buyers <strong className="text-foreground/90">{app.buyer_count_at_submission || (app.buyers || []).length}</strong>
            </span>
            <span className="text-foreground/65">
              Requested <strong className="text-foreground/90">${(app.requested_aggregate_limit || 0).toLocaleString()}</strong>
            </span>
            <span className="text-foreground/65">
              Auto-calc <span className="mono">${(app.auto_calculated_aggregate_limit || 0).toLocaleString()}</span>
            </span>
            <span className="text-foreground/65">
              Roster next-12mo <span className="mono">${Math.round(totalNextVol).toLocaleString()}</span>
            </span>
            <span className="text-foreground/55 mono">
              {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : "—"}
            </span>
            {app.reapply_ticket_number && (
              <span className="text-amber-700 mono">
                ticket {app.reapply_ticket_number}
              </span>
            )}
            {app.allianz_policy_id && (
              <span className="text-emerald-700 mono">
                policy {app.allianz_policy_id}
              </span>
            )}
          </div>
          {app.decline_reason && (
            <div className="mt-2 text-[12px] text-rose-700 bg-rose-50/60 border border-rose-200 rounded-lg px-3 py-1.5">
              <strong>Decline:</strong> {app.decline_reason}
            </div>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-foreground/35 mt-1" />
      </div>
    </button>
  );
}

// ─── Detail dialog (read-only inspector + Approve / Decline buttons) ────
function DetailDialog({ app, open, onClose, onApprove, onDecline, onQuote }) {
  const canDecide = app.status === "pending_review" || app.status === "reapply_pending";
  const canQuote = app.status === "pending_review" || app.status === "reapply_pending" || app.status === "quoted";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="mp-admin-detail"
      >
        <DialogHeader>
          <DialogTitle>
            {app.seller_company || app.seller_name} — application{" "}
            <span className="mono text-[12px]">{app.id}</span>
          </DialogTitle>
          <DialogDescription>
            Status:{" "}
            <span className="font-medium text-foreground">{app.status}</span>
            {app.reapply_count > 0 && (
              <> · reapply #{app.reapply_count}</>
            )}
            {app.reapply_ticket_number && (
              <> · support ticket{" "}
                <span className="mono">{app.reapply_ticket_number}</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Seller meta + financial ask */}
          <div className="grid grid-cols-2 gap-3 text-[12.5px]">
            <KV label="Seller" v={app.seller_company || app.seller_name} />
            <KV label="Email" v={app.seller_email} />
            <KV label="EIN" v={app.seller_ein || "—"} mono />
            <KV
              label="Submitted"
              v={app.submitted_at ? new Date(app.submitted_at).toLocaleString() : "—"}
            />
            <KV
              label="Requested aggregate limit"
              v={`$${(app.requested_aggregate_limit || 0).toLocaleString()}`}
              strong
            />
            <KV
              label="Auto-calc suggestion (30% of next-12mo)"
              v={`$${(app.auto_calculated_aggregate_limit || 0).toLocaleString()}`}
            />
            <KV
              label="Buyers on roster"
              v={app.buyer_count_at_submission || (app.buyers || []).length}
            />
            <KV
              label="Created"
              v={app.created_at ? new Date(app.created_at).toLocaleDateString() : "—"}
            />
          </div>

          {/* Reapply reason if present */}
          {app.reapply_reason && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 px-4 py-3">
              <div className="text-[11px] mono uppercase tracking-wider text-amber-700">
                Seller&apos;s reapply reason
              </div>
              <div className="text-[13px] text-foreground/85 mt-1 whitespace-pre-wrap">
                {app.reapply_reason}
              </div>
            </div>
          )}

          {/* Prior decline reason */}
          {app.decline_reason && (
            <div className="rounded-lg border border-rose-200 bg-rose-50/40 px-4 py-3">
              <div className="text-[11px] mono uppercase tracking-wider text-rose-700">
                Decline reason
              </div>
              <div className="text-[13px] text-foreground/85 mt-1">
                {app.decline_reason}
              </div>
            </div>
          )}

          {/* Approved policy details */}
          {app.status === "approved" && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 px-4 py-3 grid grid-cols-2 gap-2 text-[12.5px]">
              <KV label="Allianz policy ID" v={app.allianz_policy_id} mono strong />
              <KV label="Business unit" v={app.business_unit_code} mono />
              <KV label="Effective from" v={app.effective_from} mono />
              <KV label="Effective to"   v={app.effective_to}   mono />
              <KV
                label="Premium rate"
                v={`${((app.premium_rate || 0) * 100).toFixed(3)}%`}
              />
              <KV
                label="Decided at"
                v={app.decided_at ? new Date(app.decided_at).toLocaleString() : "—"}
              />
            </div>
          )}

          {/* Buyer roster */}
          <div>
            <div className="text-[12px] font-medium mb-2">
              Buyer roster ({(app.buyers || []).length})
            </div>
            <div className="rounded-xl border border-foreground/10 overflow-hidden">
              <table className="w-full text-[12px]" data-testid="mp-admin-roster-table">
                <thead className="bg-foreground/[0.04] text-foreground/65">
                  <tr>
                    <th className="text-left px-3 py-2">#</th>
                    <th className="text-left px-3 py-2">Company</th>
                    <th className="text-left px-3 py-2">EIN</th>
                    <th className="text-left px-3 py-2">Address</th>
                    <th className="text-right px-3 py-2">Sales last 12mo</th>
                    <th className="text-right px-3 py-2">Expected next 12mo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-foreground/10">
                  {(app.buyers || []).map((b, i) => (
                    <tr key={i} data-testid={`mp-admin-roster-row-${i}`}>
                      <td className="px-3 py-2 text-foreground/55 mono">{i + 1}</td>
                      <td className="px-3 py-2 font-medium">{b.company_name}</td>
                      <td className="px-3 py-2 mono">{b.ein}</td>
                      <td className="px-3 py-2 text-foreground/70">
                        {b.address_line1}
                        {b.address_line2 ? `, ${b.address_line2}` : ""}, {b.city},{" "}
                        {b.state} {b.postal_code} {b.country}
                      </td>
                      <td className="px-3 py-2 text-right mono">
                        ${Number(b.annual_sales_volume_last_12mo || 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right mono">
                        ${Number(b.annual_sales_volume_next_12mo || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-3 gap-2">
          <Button variant="ghost" onClick={onClose} className="rounded-full">
            Close
          </Button>
          {canQuote && (
            <Button
              variant="outline"
              onClick={onQuote}
              data-testid="mp-admin-quote-btn"
              className="rounded-full border-sky-300 text-sky-700 hover:bg-sky-50"
            >
              <DollarSign className="w-4 h-4 mr-1.5" />
              {app.status === "quoted" ? "Update quote" : "Enter quote"}
            </Button>
          )}
          {canDecide && (
            <>
              <Button
                variant="outline"
                onClick={onDecline}
                data-testid="mp-admin-decline-btn"
                className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50"
              >
                <XCircle className="w-4 h-4 mr-1.5" />
                Decline
              </Button>
              <Button
                onClick={onApprove}
                data-testid="mp-admin-approve-btn"
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <ShieldCheck className="w-4 h-4 mr-1.5" />
                Approve
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Approve drawer ─────────────────────────────────────────────────────
function QuoteDialog({ app, onClose, onQuoted }) {
  const [premium, setPremium] = useState(app.quote_premium_total ?? "");
  const [coverage, setCoverage] = useState(app.quote_coverage_limit ?? "");
  const [term, setTerm] = useState(app.quote_policy_term_months ?? 12);
  const [insurer, setInsurer] = useState(app.quote_insurer || "Allianz Trade");
  const [note, setNote] = useState(app.quote_admin_note || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!Number(premium) || !Number(coverage)) {
      toast.error("Premium and coverage limit are both required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/master-policy-applications/${app.id}/quote`, {
        premium_total: Number(premium),
        coverage_limit: Number(coverage),
        policy_term_months: Number(term) || 12,
        insurer: insurer.trim(),
        admin_note: note.trim() || null,
      });
      toast.success("Quote sent to seller");
      onQuoted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit quote");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="mp-admin-quote-dialog">
        <DialogHeader>
          <DialogTitle>Enter Allianz / underwriter quote</DialogTitle>
          <DialogDescription>
            Manually pulled from the insurer. Seller is notified immediately and
            can pay from their wallet (full or quarterly with 10% installment fee).
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Annual premium ($)</Label>
              <Input
                type="number" step="100" min="0"
                value={premium}
                onChange={(e) => setPremium(e.target.value)}
                data-testid="mp-admin-quote-premium"
                className="mono"
              />
            </div>
            <div>
              <Label className="text-xs">Aggregate coverage limit ($)</Label>
              <Input
                type="number" step="1000" min="0"
                value={coverage}
                onChange={(e) => setCoverage(e.target.value)}
                data-testid="mp-admin-quote-coverage"
                className="mono"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Policy term (months)</Label>
              <Input
                type="number" min="1" max="36"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                data-testid="mp-admin-quote-term"
                className="mono"
              />
            </div>
            <div>
              <Label className="text-xs">Insurer</Label>
              <Input
                value={insurer}
                onChange={(e) => setInsurer(e.target.value)}
                data-testid="mp-admin-quote-insurer"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Admin note (optional, visible only internally)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              data-testid="mp-admin-quote-note"
            />
          </div>
        </div>
        <DialogFooter className="pt-2 gap-2">
          <Button variant="ghost" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="mp-admin-quote-submit"
            className="rounded-full bg-sky-600 hover:bg-sky-700 text-white"
          >
            <DollarSign className="w-4 h-4 mr-1.5" />
            {busy ? "Sending…" : "Send quote to seller"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ApproveDialog({ app, onClose, onApproved }) {
  // Sensible defaults — broker policies are typically 12-mo terms starting
  // the 1st of next month, with 0.30% premium rate.
  const defaultFrom = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + 1);
    return d.toISOString().slice(0, 10);
  }, []);
  const defaultTo = useMemo(() => {
    const d = new Date(defaultFrom);
    d.setFullYear(d.getFullYear() + 1);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  }, [defaultFrom]);

  const [policyId, setPolicyId] = useState("");
  const [bu, setBu] = useState("US");
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [rate, setRate] = useState("0.30"); // percentage points
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const handle = async () => {
    setErr("");
    if (!policyId.trim()) {
      setErr("Policy ID required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/admin/master-policy-applications/${app.id}/approve`, {
        allianz_policy_id: policyId.trim(),
        business_unit_code: bu.trim().toUpperCase(),
        effective_from: from,
        effective_to: to,
        premium_rate: Number(rate) / 100, // convert pp to decimal
      });
      toast.success(`Approved · ${policyId.trim()} active for ${app.seller_company || app.seller_name}`);
      await onApproved();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="mp-admin-approve-dialog">
        <DialogHeader>
          <DialogTitle>Approve master policy</DialogTitle>
          <DialogDescription>
            Enter the Allianz-issued policy ID. This unlocks per-buyer cover
            requests for{" "}
            <span className="font-medium text-foreground">
              {app.seller_company || app.seller_name}
            </span>{" "}
            on the existing Cover API.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5">
          <div>
            <Label className="text-[12px]">Allianz policy ID</Label>
            <Input
              value={policyId}
              onChange={(e) => setPolicyId(e.target.value)}
              placeholder="e.g. US-12345-2026"
              data-testid="mp-admin-approve-policyid"
              className="mono h-10 mt-1"
              maxLength={20}
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12px]">Business unit code</Label>
              <Input
                value={bu}
                onChange={(e) => setBu(e.target.value.toUpperCase())}
                data-testid="mp-admin-approve-bu"
                placeholder="US"
                className="mono h-10 mt-1"
                maxLength={8}
              />
            </div>
            <div>
              <Label className="text-[12px]">Premium rate (% of limit)</Label>
              <Input
                type="number"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                data-testid="mp-admin-approve-rate"
                step="0.01"
                min="0"
                max="5"
                className="mono h-10 mt-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12px]">Effective from</Label>
              <Input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                data-testid="mp-admin-approve-from"
                className="h-10 mt-1"
              />
            </div>
            <div>
              <Label className="text-[12px]">Effective to</Label>
              <Input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                data-testid="mp-admin-approve-to"
                className="h-10 mt-1"
              />
            </div>
          </div>
          {err && (
            <div className="text-[12px] text-rose-700 bg-rose-50/60 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting} className="rounded-full">
            Cancel
          </Button>
          <Button
            onClick={handle}
            disabled={submitting || !policyId.trim()}
            data-testid="mp-admin-approve-submit"
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Approving…
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4 mr-1.5" /> Approve & activate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Decline drawer ─────────────────────────────────────────────────────
function DeclineDialog({ app, onClose, onDeclined }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const handle = async () => {
    setErr("");
    if (reason.trim().length < 4) {
      setErr("Reason must be at least 4 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/admin/master-policy-applications/${app.id}/decline`, {
        decline_reason: reason.trim(),
      });
      toast.success(`Declined · ${app.seller_company || app.seller_name} can reapply from the insurance tab`);
      await onDeclined();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="mp-admin-decline-dialog">
        <DialogHeader>
          <DialogTitle>Decline master policy</DialogTitle>
          <DialogDescription>
            The seller will see this reason on their insurance tab along with a
            Reapply CTA (which opens a support ticket for manual handling).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Label className="text-[12px]">Decline reason</Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Buyer concentration too high — top buyer is 60% of book. Need a more diversified roster."
            rows={6}
            maxLength={500}
            data-testid="mp-admin-decline-reason"
            className="text-[13px]"
          />
          <div className="text-[11px] text-foreground/55 text-right">
            {reason.length}/500
          </div>
          {err && (
            <div className="text-[12px] text-rose-700 bg-rose-50/60 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting} className="rounded-full">
            Cancel
          </Button>
          <Button
            onClick={handle}
            disabled={submitting || reason.trim().length < 4}
            data-testid="mp-admin-decline-submit"
            className="rounded-full bg-rose-600 hover:bg-rose-700 text-white"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Declining…
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 mr-1.5" /> Decline application
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Small helpers ──────────────────────────────────────────────────────
function KV({ label, v, mono = false, strong = false }) {
  return (
    <div>
      <div className="text-[10.5px] mono uppercase tracking-wider text-foreground/45">
        {label}
      </div>
      <div
        className={`text-[13px] ${mono ? "mono" : ""} ${strong ? "font-medium" : ""}`}
      >
        {v ?? "—"}
      </div>
    </div>
  );
}
